import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ZoomIn, ZoomOut } from 'lucide-react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';
import countryColors from '../../config/country_colors.json';

interface CountryPin {
  country: string;
  latitude: number;
  longitude: number;
  year: string;
}

interface WorldMapProps {
  countries: CountryPin[];
  onCountryClick: (country: string) => void;
}

// TopoJSON URL for world map data (Natural Earth)
const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const WorldMap: React.FC<WorldMapProps> = ({ countries, onCountryClick }) => {
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1.5);
  const [center, setCenter] = useState<[number, number]>([30, 20]);

  const handleMarkerMouseEnter = (country: string) => {
    setHoveredCountry(country);
  };

  const handleMarkerMouseLeave = () => {
    setHoveredCountry(null);
  };

  const handleZoomIn = () => {
    if (zoom < 8) {
      setZoom(zoom * 1.5);
    }
  };

  const handleZoomOut = () => {
    if (zoom > 1.5) {
      setZoom(Math.max(1.5, zoom / 1.5));
    }
  };

  const handleMoveEnd = (position: { coordinates: [number, number]; zoom: number }) => {
    setCenter(position.coordinates);
    setZoom(position.zoom);
  };

  return (
    <div className="relative w-full h-[600px] gradient-gallery-light rounded-2xl overflow-hidden shadow-2xl">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 147,
          center: [30, 20], // Center on Asia/Africa/Europe region
        }}
        className="w-full h-full"
      >
        <ZoomableGroup
          zoom={zoom}
          minZoom={1.5}
          maxZoom={8}
          center={center}
          onMoveEnd={handleMoveEnd}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }: { geographies: any[] }) =>
              geographies.map((geo: any) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#e0e7ff"
                  stroke="#c7d2fe"
                  strokeWidth={0.5}
                  className="dark:fill-slate-700 dark:stroke-slate-600 transition-all duration-300 hover:fill-indigo-200 dark:hover:fill-slate-600 outline-none focus:outline-none"
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>

          {/* Country Markers */}
          {countries.map((pin, index) => {
            const isHovered = hoveredCountry === pin.country;
            // Calculate scale factor to maintain consistent size across zoom levels
            // Slightly bigger pins when zoomed out, smaller when zoomed in
            const pinScale = 1.2 / Math.pow(zoom, 0.4);
            
            return (
              <Marker
                key={pin.country}
                coordinates={[pin.longitude, pin.latitude]}
              >
                <motion.g
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ 
                    scale: pinScale,
                    opacity: 1,
                  }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.3,
                  }}
                  onMouseEnter={() => handleMarkerMouseEnter(pin.country)}
                  onMouseLeave={handleMarkerMouseLeave}
                  onClick={() => onCountryClick(pin.country)}
                  className="cursor-pointer"
                  style={{ pointerEvents: 'all' }}
                >
                  {/* Hover pulse ring */}
                  {isHovered && (
                    <motion.circle
                      r={8}
                      fill="none"
                      stroke="#ea580c"
                      strokeWidth={1.5}
                      initial={{ scale: 0.8, opacity: 0.8 }}
                      animate={{
                        scale: [1, 1.5],
                        opacity: [0.6, 0],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: 'easeOut',
                      }}
                    />
                  )}

                  {/* Main pin circle */}
                  <motion.circle
                    r={6}
                    fill="#ea580c"
                    stroke="#fff"
                    strokeWidth={2}
                    animate={{
                      r: isHovered ? 7 : 6,
                      strokeWidth: isHovered ? 3 : 2,
                    }}
                    transition={{ duration: 0.2 }}
                  />
                  
                  {/* Inner dot */}
                  <motion.circle
                    r={3}
                    fill="#fff"
                    animate={{
                      r: isHovered ? 3.5 : 3,
                    }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.g>
              </Marker>
            );
          })}

        </ZoomableGroup>
      </ComposableMap>

      {/* Simple Tooltip positioned relative to map container */}
      {hoveredCountry && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none">
          <motion.div
            className="px-6 py-3 text-white text-base font-bold rounded-lg shadow-lg whitespace-nowrap"
            style={{
              backgroundColor: (countryColors as any)[hoveredCountry]?.primary || '#ea580c',
            }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="text-center">
              <div className="text-lg font-bold">{hoveredCountry}</div>
              <div 
                className="text-sm font-semibold"
                style={{
                  color: (countryColors as any)[hoveredCountry]?.secondary || '#000000',
                }}
              >
                {countries.find((c) => c.country === hoveredCountry)?.year}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Zoom Controls */}
      <div className="absolute bottom-4 left-4 flex flex-col gap-2">
        <motion.button
          onClick={handleZoomIn}
          disabled={zoom >= 8}
          className="p-3 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-lg hover:bg-white dark:hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Zoom in"
        >
          <ZoomIn className="w-5 h-5 text-slate-700 dark:text-slate-300 group-hover:text-amber-600 dark:group-hover:text-amber-500 transition-colors" />
        </motion.button>
        
        <motion.button
          onClick={handleZoomOut}
          disabled={zoom <= 1}
          className="p-3 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-lg hover:bg-white dark:hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Zoom out"
        >
          <ZoomOut className="w-5 h-5 text-slate-700 dark:text-slate-300 group-hover:text-amber-600 dark:group-hover:text-amber-500 transition-colors" />
        </motion.button>

        {/* Zoom Level Indicator */}
        <div className="px-3 py-1 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-lg text-center">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
            {zoom.toFixed(1)}x
          </span>
        </div>
      </div>
    </div>
  );
};

export default WorldMap;
